import { useState, useEffect, useCallback, useRef } from 'react';
import { Unsubscribe } from 'firebase/firestore';
import {
  addToQueue,
  removeFromQueue,
  findOpponent,
  createMatch,
  listenToMatch,
  setPlayerReady,
  submitAnswer,
  advanceQuestion,
  updateConnectionStatus,
  forfeitMatch,
  checkOpponentQueueForMatch,
  updateOwnStatsAfterMatch,
} from '../services/matchmaking.service';
import { Match, Question, GameAnswer } from '../types';
import { useAuth } from '../contexts/AuthContext';

export type MatchmakingState =
  | 'idle'
  | 'searching'
  | 'found'
  | 'waiting'
  | 'playing'
  | 'completed'
  | 'error';

interface UseMatchmakingReturn {
  state: MatchmakingState;
  match: Match | null;
  error: string | null;
  isPlayer1: boolean;
  opponentName: string;
  joinQueue: () => Promise<void>;
  leaveQueue: () => Promise<void>;
  markReady: () => Promise<void>;
  submitPlayerAnswer: (answer: GameAnswer) => Promise<void>;
  nextQuestion: () => Promise<void>;
  forfeit: () => Promise<void>;
}

/**
 * Custom hook for managing matchmaking and multiplayer game state
 */
export const useMatchmaking = (questions: Question[]): UseMatchmakingReturn => {
  const { currentUser: user } = useAuth();
  const [state, setState] = useState<MatchmakingState>('idle');
  const [match, setMatch] = useState<Match | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matchUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const searchIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldCleanupQueueRef = useRef<boolean>(false);
  const userRef = useRef(user);
  const matchRef = useRef(match);

  // Keep user and match refs updated
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    matchRef.current = match;
  }, [match]);

  // Track whether we should cleanup queue (avoid closure issues)
  useEffect(() => {
    shouldCleanupQueueRef.current = (state === 'searching' || state === 'found');
    console.log('🔄 shouldCleanupQueue updated to:', shouldCleanupQueueRef.current, 'state:', state);
  }, [state]);

  // Cleanup ONLY on unmount (not on state changes!)
  useEffect(() => {
    return () => {
      // Clean up queue entry if still searching (use ref to get latest value)
      const matchUser = userRef.current;
      const currentMatch = matchRef.current;

      if (matchUser && shouldCleanupQueueRef.current) {
        console.log('🧹 Cleaning up queue on unmount, shouldCleanup:', shouldCleanupQueueRef.current);
        removeFromQueue(matchUser.uid).catch(console.error);
      }

      if (matchUnsubscribeRef.current) {
        matchUnsubscribeRef.current();
      }
      if (searchIntervalRef.current) {
        clearInterval(searchIntervalRef.current);
      }
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Mark as disconnected when component unmounts
      if (currentMatch && matchUser) {
        console.log('🧹 Component unmounting, marking as disconnected');
        updateConnectionStatus(currentMatch.id, matchUser.uid, false).catch(console.error);
      }
    };
  }, []); // Empty deps - only run on mount/unmount!

  // Update connection status when match changes
  useEffect(() => {
    if (match && user && state === 'playing') {
      updateConnectionStatus(match.id, user.uid, true);
    }
  }, [match, user, state]);

  // Heartbeat mechanism - keep connection status alive
  useEffect(() => {
    if (match && user && state === 'playing') {
      // Send heartbeat every 10 seconds
      heartbeatIntervalRef.current = setInterval(() => {
        console.log('💓 Sending heartbeat...');
        updateConnectionStatus(match.id, user.uid, true).catch((err) => {
          console.error('❌ Heartbeat failed:', err);
        });
      }, 10000); // 10 seconds

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };
    }
  }, [match, user, state]);

  // Handle tab visibility changes (mark disconnected when tab hidden)
  useEffect(() => {
    if (!match || !user || state !== 'playing') return;

    const handleVisibilityChange = () => {
      const currentMatch = matchRef.current;
      const matchUser = userRef.current;

      if (!currentMatch || !matchUser) return;

      if (document.hidden) {
        console.log('👁️ Tab hidden, marking as disconnected');
        updateConnectionStatus(currentMatch.id, matchUser.uid, false).catch(console.error);
      } else {
        console.log('👁️ Tab visible, marking as connected');
        updateConnectionStatus(currentMatch.id, matchUser.uid, true).catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [match, user, state]);

  // Handle beforeunload (browser closing/refresh)
  useEffect(() => {
    if (!match || !user || state !== 'playing') return;

    const handleBeforeUnload = () => {
      const currentMatch = matchRef.current;
      const matchUser = userRef.current;

      if (!currentMatch || !matchUser) return;

      console.log('🚪 Browser closing, marking as disconnected');
      // Use sendBeacon for reliable disconnect on page unload
      const matchId = currentMatch.id;
      const playerId = matchUser.uid;

      // Mark disconnected (best effort - may not complete)
      updateConnectionStatus(matchId, playerId, false).catch(console.error);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [match, user, state]);

  // Listen to match updates
  const startMatchListener = useCallback((matchId: string) => {
    console.log('👂 Starting match listener for:', matchId);

    if (matchUnsubscribeRef.current) {
      matchUnsubscribeRef.current();
    }

    matchUnsubscribeRef.current = listenToMatch(matchId, (updatedMatch) => {
      console.log('📡 Match update received:', updatedMatch.status);
      console.log('🎮 Player states - P1 ready:', updatedMatch.player1.ready, 'P2 ready:', updatedMatch.player2?.ready);
      setMatch(updatedMatch);

      // Update state based on match status
      if (updatedMatch.status === 'in_progress' && state !== 'playing') {
        setState('playing');
      } else if (updatedMatch.status === 'completed') {
        setState('completed');

        // Update own stats when match completes
        if (user) {
          console.log('📊 Match completed, updating own stats...');
          updateOwnStatsAfterMatch(matchId, user.uid).catch((err) => {
            console.error('❌ Error updating own stats:', err);
          });
        }

        if (matchUnsubscribeRef.current) {
          matchUnsubscribeRef.current();
          matchUnsubscribeRef.current = null;
        }
      }

      // Check opponent connection
      if (user && updatedMatch.status === 'in_progress') {
        const isPlayer1 = updatedMatch.player1.uid === user.uid;
        const opponent = isPlayer1 ? updatedMatch.player2 : updatedMatch.player1;

        if (opponent && !opponent.connected && !disconnectTimeoutRef.current) {
          console.log('⚠️ Opponent disconnected, starting forfeit timer');
          disconnectTimeoutRef.current = setTimeout(() => {
            console.log('⏰ Forfeit timeout reached');
            forfeitMatch(matchId, opponent.uid);
          }, 30000); // 30 second timeout
        } else if (opponent && opponent.connected && disconnectTimeoutRef.current) {
          console.log('✅ Opponent reconnected, canceling forfeit timer');
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }
      }
    });
  }, [user, state]);

  // Join matchmaking queue
  const joinQueue = useCallback(async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      setState('searching');
      setError(null);

      console.log('🔍 Joining matchmaking queue...');

      // Add to queue
      await addToQueue(user.uid, user.displayName, user.avatar, user.rank);

      // Define the search function - returns true if opponent found
      const searchForOpponent = async (): Promise<boolean> => {
        console.log('🔎 Searching for opponent...');

        const opponent = await findOpponent(user.uid);

        if (opponent) {
          console.log('✅ Found opponent:', opponent.displayName);
          console.log('🔑 UID comparison - Me:', user.uid, 'Opponent:', opponent.uid, 'I create?', user.uid < opponent.uid);

          // Stop polling
          if (searchIntervalRef.current) {
            clearInterval(searchIntervalRef.current);
            searchIntervalRef.current = null;
          }

          // Only create match if current user has lower UID (prevents race condition)
          // This ensures only one player creates the match
          if (user.uid < opponent.uid) {
            console.log('🎮 Creating match (I have priority)...');

            // Create match
            const matchId = await createMatch(
              {
                uid: user.uid,
                displayName: user.displayName,
                avatar: user.avatar,
              },
              {
                uid: opponent.uid,
                displayName: opponent.displayName,
                avatar: opponent.avatar,
              },
              questions
            );

            setState('found');

            // Start listening to the match
            startMatchListener(matchId);
            setState('waiting');

            // Don't remove from queue yet - will be cleaned up when match starts
            // This allows Player 2 to still find us in the queue
          } else {
            console.log('⏳ Waiting for opponent to create match...');
            console.log('🔍 Polling opponent queue entry for matchId...');
            setState('found');

            // Track if match was found (to avoid timeout false positive)
            let matchFound = false;
            let pollCount = 0;

            // Poll for match creation by opponent (check opponent's queue entry)
            const checkMatchInterval = setInterval(async () => {
              pollCount++;
              console.log(`🔄 Poll attempt ${pollCount} - checking opponent queue for matchId...`);

              const matchId = await checkOpponentQueueForMatch(opponent.uid);

              if (matchId) {
                matchFound = true;
                clearInterval(checkMatchInterval);
                console.log('🎉 Match created by opponent:', matchId);

                startMatchListener(matchId);
                setState('waiting');

                // Don't remove from queue - will be cleaned up when both ready
              } else {
                console.log(`⏳ Poll ${pollCount}: No matchId yet, will retry in 500ms`);
              }
            }, 500); // Check every 500ms

            // Timeout after 15 seconds (increased for reliability)
            setTimeout(() => {
              clearInterval(checkMatchInterval);
              if (!matchFound) {
                console.log('⏰ Timeout waiting for match creation');
                setError('Match creation timeout - please try again');
                setState('error');
              }
            }, 15000);
          }

          return true; // Opponent found
        }

        return false; // No opponent yet
      };

      // Search immediately once
      const foundOpponent = await searchForOpponent();

      // Only set up polling interval if we didn't find an opponent on first try
      if (!foundOpponent) {
        searchIntervalRef.current = setInterval(searchForOpponent, 1000); // Reduced from 2000ms to 1000ms for faster matchmaking
      }
    } catch (err) {
      console.error('❌ Error joining queue:', err);
      setError('Failed to join matchmaking queue');
      setState('error');
    }
  }, [user, questions, startMatchListener]);

  // Leave matchmaking queue
  const leaveQueue = useCallback(async () => {
    if (!user) return;

    try {
      console.log('🚪 Leaving matchmaking queue...');

      await removeFromQueue(user.uid);

      if (searchIntervalRef.current) {
        clearInterval(searchIntervalRef.current);
        searchIntervalRef.current = null;
      }

      setState('idle');
      setError(null);
    } catch (err) {
      console.error('❌ Error leaving queue:', err);
      setError('Failed to leave queue');
    }
  }, [user]);

  // Mark player as ready
  const markReady = useCallback(async () => {
    if (!user || !match) {
      console.log('⚠️ Cannot mark ready: user or match not available');
      return;
    }

    try {
      console.log('✅ Marking player ready...');
      await setPlayerReady(match.id, user.uid);
    } catch (err) {
      console.error('❌ Error marking ready:', err);
      setError('Failed to mark ready');
    }
  }, [user, match]);

  // Submit answer
  const submitPlayerAnswer = useCallback(
    async (answer: GameAnswer) => {
      if (!user || !match) return;

      try {
        console.log('📝 Submitting answer...');
        await submitAnswer(match.id, user.uid, answer);
      } catch (err) {
        console.error('❌ Error submitting answer:', err);
        setError('Failed to submit answer');
      }
    },
    [user, match]
  );

  // Advance to next question
  const nextQuestion = useCallback(async () => {
    if (!match) return;

    try {
      console.log('⏭️ Advancing to next question...');
      await advanceQuestion(match.id);
    } catch (err) {
      console.error('❌ Error advancing question:', err);
      setError('Failed to advance question');
    }
  }, [match]);

  // Forfeit match
  const forfeit = useCallback(async () => {
    if (!user || !match) return;

    try {
      console.log('🏳️ Forfeiting match...');
      await forfeitMatch(match.id, user.uid);
      setState('completed');
    } catch (err) {
      console.error('❌ Error forfeiting:', err);
      setError('Failed to forfeit');
    }
  }, [user, match]);

  // Determine if current user is player1
  const isPlayer1 = user && match ? match.player1.uid === user.uid : false;

  // Get opponent name
  const opponentName =
    user && match
      ? isPlayer1
        ? match.player2?.displayName || 'Opponent'
        : match.player1.displayName
      : 'Opponent';

  return {
    state,
    match,
    error,
    isPlayer1,
    opponentName,
    joinQueue,
    leaveQueue,
    markReady,
    submitPlayerAnswer,
    nextQuestion,
    forfeit,
  };
};
